import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { useWishlist } from "../../contexts/WishlistContext";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ShoppingCart, User, Menu, X, LogOut, Package, Users, Settings, Heart, Instagram, Facebook } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSocialLinks } from "../../hooks/useSocialLinks";

export const Navigation = () => {
  const { user, logout } = useAuth();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const social = useSocialLinks();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = [
    { href: "/artist", label: "The Artist" },
    { href: "/the-collection", label: "The Collection" },
    { href: "/commissions", label: "Commissions" },
    { href: "/auctions", label: "Auctions" },
    { href: "/quote-request", label: "Trade Enquiries" },
  ];

  return (
  <>
    <motion.nav
      initial={{ y: -100, opacity: 0, x: "-50%" }}
      animate={{ y: 0, opacity: 1, x: "-50%" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-6 left-1/2 w-[92%] max-w-5xl rounded-2xl md:rounded-full glass z-50 px-5 md:px-8 py-3 md:py-4"
      data-testid="main-navigation"
    >
      <div className="flex justify-between items-center">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-2 group flex-shrink-0"
          data-testid="nav-logo"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_8576cbf0-8d30-4753-9193-cebdf96ed739/artifacts/m5p2j94m_Asset%205.png" 
            alt="Wallaby" 
            className="h-7 md:h-[37px] w-auto"
          />
          <img 
            src="/arawol-wordmark.png" 
            alt="Arawol" 
            className="h-5 md:h-7 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6 ml-8">          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="text-sm tracking-wide text-muted-foreground hover:text-foreground transition-colors duration-300"
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
            </Link>
          ))}
          {!user && (
            <Link
              to="/login"
              className="text-sm tracking-wide font-medium text-primary hover:text-primary/80 transition-colors duration-300"
              data-testid="nav-login-link"
            >
              Sign In
            </Link>
          )}
        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-2">
          {/* Social Links */}
          {social.instagram && (
          <a
            href={social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-full hover:bg-accent transition-colors"
            aria-label="Instagram"
            data-testid="nav-instagram"
          >
            <Instagram className="h-4 w-4 text-muted-foreground hover:text-foreground" strokeWidth={1.5} />
          </a>
          )}
          {social.facebook && (
          <a
            href={social.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-full hover:bg-accent transition-colors"
            aria-label="Facebook"
            data-testid="nav-facebook"
          >
            <Facebook className="h-4 w-4 text-muted-foreground hover:text-foreground" strokeWidth={1.5} />
          </a>
          )}
          
          <div className="w-px h-5 bg-border mx-1" />
          
          {/* Wishlist */}
          <Link to="/wishlist" data-testid="nav-wishlist-btn">
            <Button variant="ghost" size="icon" className="rounded-full hover:bg-accent relative">
              <Heart className="h-5 w-5" strokeWidth={1.5} />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-pink-500 text-[10px] font-medium text-white flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Button>
          </Link>
          
          {/* Cart - only for wholesale/admin users */}
          {user && (user.role === "admin" || (user.role === "wholesale" && user.wholesale_status === "approved")) && (
          <Link to="/cart" data-testid="nav-cart-btn">
            <Button variant="ghost" size="icon" className="rounded-full hover:bg-accent">
              <ShoppingCart className="h-5 w-5" strokeWidth={1.5} />
            </Button>
          </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full hover:bg-accent"
                  data-testid="nav-user-menu"
                >
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="cursor-pointer" data-testid="nav-dashboard-link">
                    <Package className="mr-2 h-4 w-4" />
                    My Orders
                  </Link>
                </DropdownMenuItem>
                {user.role === "promoter" && (
                  <DropdownMenuItem asChild>
                    <Link to="/promoter" className="cursor-pointer" data-testid="nav-promoter-link">
                      <Users className="mr-2 h-4 w-4" />
                      Promoter Dashboard
                    </Link>
                  </DropdownMenuItem>
                )}
                {user.role === "marketing_manager" && (
                  <DropdownMenuItem asChild>
                    <Link to="/marketing" className="cursor-pointer" data-testid="nav-marketing-link">
                      <Settings className="mr-2 h-4 w-4" />
                      Marketing Dashboard
                    </Link>
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer" data-testid="nav-admin-link">
                      <Settings className="mr-2 h-4 w-4" />
                      Admin Dashboard
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer" data-testid="nav-logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="nav-login-btn">
              <Button className="rounded-full px-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 btn-hover">
                Sign In
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-2">
          {user && (user.role === "admin" || (user.role === "wholesale" && user.wholesale_status === "approved")) && (
          <Link to="/cart" data-testid="nav-cart-btn-mobile">
            <Button variant="ghost" size="icon" className="rounded-full h-9 w-9">
              <ShoppingCart className="h-5 w-5" strokeWidth={1.5} />
            </Button>
          </Link>
          )}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center justify-center h-9 w-9 rounded-full bg-primary/10"
            data-testid="nav-mobile-menu-btn"
          >
            <Menu className="h-5 w-5 text-foreground" />
          </button>
        </div>
      </div>
    </motion.nav>

    {/* Full-Screen Mobile Menu Overlay */}
    <AnimatePresence>
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[60] bg-background/98 backdrop-blur-xl md:hidden"
          data-testid="mobile-menu-overlay"
        >
          {/* Close Button */}
          <div className="flex justify-between items-center px-6 pt-8">
            <Link 
              to="/" 
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_8576cbf0-8d30-4753-9193-cebdf96ed739/artifacts/m5p2j94m_Asset%205.png" 
                alt="Wallaby" 
                className="h-8 w-auto"
              />
              <img src="/arawol-wordmark.png" alt="Arawol" className="h-6 w-auto" />
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full h-10 w-10"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-mobile-close-btn"
            >
              <X className="h-6 w-6" />
            </Button>
          </div>

          {/* Menu Links */}
          <div className="flex flex-col px-6 pt-12 gap-1">
            {navLinks.map((link, i) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Link
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 text-2xl font-bold tracking-wide text-foreground hover:text-primary transition-colors"
                  data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}

            <div className="mt-6 pt-6 border-t border-border space-y-1">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-base text-muted-foreground hover:text-foreground"
                  >
                    My Orders
                  </Link>
                  <Link
                    to="/wishlist"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-base text-muted-foreground hover:text-foreground"
                  >
                    Wishlist
                  </Link>
                  {user.role === "promoter" && (
                    <Link to="/promoter" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base text-muted-foreground hover:text-foreground">
                      Promoter Dashboard
                    </Link>
                  )}
                  {user.role === "marketing_manager" && (
                    <Link to="/marketing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base text-muted-foreground hover:text-foreground">
                      Marketing Dashboard
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base text-muted-foreground hover:text-foreground">
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="block py-2 text-base text-destructive hover:text-destructive/80"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full rounded-full mt-2 py-5 text-base bg-primary text-primary-foreground" data-testid="nav-mobile-signin">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>

            {/* Social links */}
            <div className="flex items-center gap-4 mt-8">
              {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-accent">
                <Instagram className="h-5 w-5 text-muted-foreground" />
              </a>
              )}
              {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-accent">
                <Facebook className="h-5 w-5 text-muted-foreground" />
              </a>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </>
  );
};

export default Navigation;
